import { ColumnConfig, Order } from "@app/components/table/table.component";
import Table from "@app/components/table";
import TitleDescription from "@app/components/title-description";
import { useEffect, useState } from "react";
import {
  BookRevenue,
  CreateWithdrawForm,
  GetRevenueRequestParams,
  Meta,
} from "@app/types";
import { RequestParams } from "@core/services/http/http.type";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
} from "@app/constants";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { openPortalDialog } from "@app/services/modal.service";
import { addToast } from "@app/components/toast/toast.service";
import { formatMoneyVN } from "@app/utils/util";
import WalletService from "@app/services/http/wallet.service";
import WithdrawIcon from "@app/icons/withdraw";
import WithdrawModal from "@app/components/withdraw-modal";
import { useNavigate } from "react-router-dom";

function IdColumnTable({ data }: { data: BookRevenue["items"][0] }) {
  return <div>{data?.bookId}</div>;
}

function BookNameColumnTable({ data }: { data: BookRevenue["items"][0] }) {
  const thumbnail = data?.thumbnails?.[0] || "";

  return (
    <div className="flex items-center gap-3">
      {thumbnail && (
        <img
          src={thumbnail}
          alt={data?.bookName}
          className="w-12 h-12 object-cover rounded"
        />
      )}
      <div className="truncate">{data?.bookName}</div>
    </div>
  );
}

function RevenueRetailColumn({ data }: { data: BookRevenue["items"][0] }) {
  return <div>{formatMoneyVN(data?.retailRevenue || 0)}đ</div>;
}

function RevenueVipColumn({ data }: { data: BookRevenue["items"][0] }) {
  return <div>{formatMoneyVN(data?.vipRevenue || 0)}đ</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "bookId",
    width: "10%",
    tdClassName: "truncate min-w-[80px]",
    hasOrder: true,
    component: IdColumnTable,
  },
  {
    label: "Tên sách",
    dataKey: "bookName",
    width: "70%",
    tdClassName: "min-w-[200px]",
    hasOrder: true,
    component: BookNameColumnTable,
  },
  {
    label: "Bán lẻ",
    dataKey: "retailRevenue",
    width: "15%",
    tdClassName: "truncate min-w-[100px]",
    hasOrder: true,
    component: RevenueRetailColumn,
  },
  {
    label: "VIP",
    dataKey: "vipRevenue",
    width: "15%",
    tdClassName: "truncate min-w-[100px]",
    hasOrder: true,
    component: RevenueVipColumn,
  },
];

const WalletManagement = () => {
  const [bookRevenue, setBookRevenue] = useState<BookRevenue[]>([]);
  const [requestParams, setRequestParams] = useState<GetRevenueRequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const [meta, setMeta] = useState<Meta>();
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [update, forceUpdate] = useForceUpdate();
  const navigate = useNavigate();

  useEffect(() => {
    WalletService.getRevenue(requestParams).then((res) => {
      setBookRevenue(res.data.items);
      setTotalRevenue(res.data.totalRevenue);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleWithdrawClick = () => {
    const obs = openPortalDialog(WithdrawModal);

    obs.afterClosed().subscribe((data: { withdraw: CreateWithdrawForm }) => {
      if (data?.withdraw?.money) {
        WalletService.createWithdraw(data.withdraw)
          .then((res) => {
            addToast({ text: res.data.message });
            forceUpdate();
          })
          .catch(() => {
            navigate(`/${RoutePath.ACCOUNT_INFO}`);
          });
      }
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="min-w-[220px] w-full sm:w-auto">
          <div className="inline-block bg-white border border-[#e5e7eb] rounded-xl px-5 py-3 shadow-sm">
            <p className="text-xs text-[#6b7280] uppercase tracking-wide">Tiền có thể rút</p>
            <p className="text-2xl font-semibold text-[#111827]">{formatMoneyVN(totalRevenue)}đ</p>
          </div>
        </div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white shadow-md"
            onClick={handleWithdrawClick}
          >
            <div className="flex gap-4 items-center">
              <WithdrawIcon color="white" />
              <div>Rút tiền</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription title="Doanh thu" onInputChange={onSearchChange} />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={bookRevenue}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            tableClassName="w-full"
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default WalletManagement;
